"use client"

import { LegalLink } from "@/components/legal/legal-link"
import { useEffect, useState } from "react"
import { Check, X } from "lucide-react"
import { DialogDescription, DialogTitle } from "@/components/ui/overlay/dialog"
import { OnboardingDialog, useOnboardingEscape } from "@/components/player-onboarding/onboarding-dialog"
import { WhoopConnectButton } from "@/components/features/fitness/whoop-connect-button"
import { InstagramConnectButton } from "@/components/features/social/instagram-connect-button"
import { MentalHealthQuestionnaire } from "@/components/features/mental/mental-health-questionnaire"
import apiClient from "@/lib/api/api-client"
import { hardNavigate } from "@/lib/navigation"
import {
  PLAYER_INTRO_STEPS,
  ageFromIso,
  type PlayerSetupConsents,
  type PlayerSetupDetail,
} from "@/types/player-setup"

const primaryClass =
  "min-h-[44px] rounded-[10px] border-0 bg-[#3B5FC4] px-[22px] text-[15px] font-semibold text-white hover:bg-[#2C4A9E] disabled:cursor-not-allowed disabled:bg-[#E5E7EB] disabled:text-[#6B7280] dark:bg-primary dark:hover:bg-primary/90 dark:disabled:bg-muted dark:disabled:text-muted-foreground"
const quietClass =
  "min-h-[44px] rounded-[10px] border border-[#E5E7EB] bg-white px-[18px] text-[15px] font-medium text-[#1F2328] dark:border-border dark:bg-card dark:text-foreground"

const IG_CHECKS = [
  {
    title: "It’s a professional Instagram account",
    sub: "Business or Creator. Personal accounts can’t be connected.",
    steps: [
      "Open Instagram and go to your profile.",
      "Open the menu, then Account type and tools.",
      "Tap Switch to professional account and pick Creator or Business.",
    ],
  },
  {
    title: "It’s linked to a Facebook Page",
    sub: "Instagram shares story data through the linked Page.",
    steps: [
      "In Instagram, tap Edit profile.",
      "Under business information, tap Page.",
      "Connect an existing Facebook Page, or create a new one.",
    ],
  },
  {
    title: "You can log in as admin of that Page",
    sub: "The next screen asks for a Facebook login, not Instagram.",
    steps: [
      "Use the Facebook account that created the Page.",
      "Not yours? Ask the Page owner to give you full control.",
    ],
  },
]

const CONSENT_COPY = [
  {
    key: "publicCareer" as const,
    title: "Public career data",
    short: "Your match statistics and news, from public sources. Your agent sees them too.",
    detail: "Match statistics, ratings and news articles about you, collected from public sources. We use them to show your performance and media coverage. Your agent sees them too.",
    without: "Without it: you and your agent can't see your statistics and news.",
  },
  {
    key: "whoop" as const,
    title: "WHOOP data",
    short: "Your WHOOP health data, for fitness, recovery and career estimates. Your agent sees it too.",
    detail: "Heart rate, HRV, sleep, recovery, strain and workouts. This is health data. We use it to show your fitness and recovery, and in your career estimates. Your agent sees it too.",
    without: "Without it: you and your agent can't see your fitness and recovery.",
  },
  {
    key: "motivation" as const,
    title: "Motivation questionnaire",
    short: "Your answers and scores on motivation and wellbeing. Your agent sees them too.",
    detail: "Your answers and scores on self-regulation, coping and motivation. This is data about your wellbeing. We use it to track your motivation, and in your career estimates. Your agent sees your answers and scores.",
    without: "Without it: you and your agent can't track your motivation.",
  },
  {
    key: "aiAssistant" as const,
    title: "AI Analyst",
    short: "Chat with our AI. Your questions are sent to Mistral AI.",
    detail: "Ask questions in the app’s AI chat. Your questions are sent to Mistral AI, a French company, to generate answers. Mistral keeps them for up to 30 days and does not use them for training. SigmAI does not save your chats.",
    without: "Without it: you can't use the AI Analyst.",
  },
  {
    key: "improveModels" as const,
    title: "Help improve SigmAI",
    short: "Your data, anonymised, to improve our models.",
    detail: "We use your data, including health data, in anonymised form to improve our models, for example the ones that estimate career steps. It can no longer be traced back to you.",
    without: "This does not change how the app works for you.",
    adultOnly: true,
  },
]

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("")
}

function money(value?: number | null) {
  if (value == null || Number.isNaN(value)) return ""
  return String(value)
}

function parseMoney(value: string): number | null {
  const cleaned = value.replace(/\s/g, "").replace(/€/g, "").replace(",", "")
  if (!cleaned) return null
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

function splitDate(iso?: string | null) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "")
  return { year: match?.[1] ?? "", month: match?.[2] ?? "", day: match?.[3] ?? "" }
}

function doneStatus(status?: string) {
  return status === "completed" || status === "skipped"
}

export function PlayerSetupWizard({
  open,
  setup: incoming,
  onUpdated,
  onDismiss,
}: {
  open: boolean
  setup: PlayerSetupDetail
  onUpdated: (next: PlayerSetupDetail) => void
  onDismiss: () => void
}) {
  const [setup, setSetup] = useState(incoming)
  const [stepId, setStepId] = useState(incoming.resumeStep || PLAYER_INTRO_STEPS[0])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [day, setDay] = useState(splitDate(incoming.birthDate).day)
  const [month, setMonth] = useState(splitDate(incoming.birthDate).month)
  const [year, setYear] = useState(splitDate(incoming.birthDate).year)
  const [terms, setTerms] = useState(incoming.termsCurrent)
  const agentsAlreadyChosen = incoming.started || doneStatus(incoming.steps.find((step) => step.id === "player_agent_access")?.status)
  const [agentChoice, setAgentChoice] = useState<Record<string, boolean | null>>(() =>
    Object.fromEntries(incoming.agents.map((agent) => [agent.id, agentsAlreadyChosen ? true : null])),
  )
  const [consents, setConsents] = useState(incoming.consents)
  const [consentMore, setConsentMore] = useState<string | null>(null)
  const [consentSheet, setConsentSheet] = useState(false)
  const [help, setHelp] = useState(false)
  const [income, setIncome] = useState(money(incoming.financial?.netMonthlyIncome))
  const [savings, setSavings] = useState(money(incoming.financial?.monthlySavings))
  const [liquid, setLiquid] = useState(money(incoming.financial?.liquidWealth))
  const [whoopPhase, setWhoopPhase] = useState<"idle" | "error" | "connected">(incoming.whoopConnected ? "connected" : "idle")
  const [igPhase, setIgPhase] = useState<"checks" | "error" | "connected">(incoming.instagramConnected ? "connected" : "checks")
  const [igHandle, setIgHandle] = useState(incoming.instagramUsername ?? "")
  const [motivationDone, setMotivationDone] = useState(doneStatus(incoming.steps.find((step) => step.id === "player_motivation")?.status))

  const birthIso = year.length === 4 && month.length > 0 && day.length > 0
    ? `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
    : ""
  const age = birthIso ? ageFromIso(birthIso) : null
  const tooYoung = age != null && age < 16
  const taskIds = setup.started ? setup.steps.map((step) => step.id) : []
  const phase = stepId === "review" ? [] : (PLAYER_INTRO_STEPS as readonly string[]).includes(stepId) || !setup.started
    ? [...PLAYER_INTRO_STEPS]
    : taskIds
  const phaseIndex = phase.indexOf(stepId)
  const nav = stepId === "review" ? [] : [...PLAYER_INTRO_STEPS, ...taskIds]
  const navIndex = nav.indexOf(stepId)
  const lastTask = taskIds.length > 0 && stepId === taskIds[taskIds.length - 1]
  const skippedWhoop = !consents.whoop
  const skippedQuest = !consents.motivation

  useEffect(() => {
    setHelp(false)
  }, [stepId])

  function apply(next: PlayerSetupDetail) {
    setSetup(next)
    onUpdated(next)
  }

  async function leave() {
    if (!setup.started) {
      await apiClient.auth.logout()
      hardNavigate("/login", { replace: true })
      return
    }
    onDismiss()
  }

  async function run(action: () => Promise<PlayerSetupDetail>) {
    setBusy(true)
    setError(null)
    try {
      apply(await action())
      return true
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong")
      return false
    } finally {
      setBusy(false)
    }
  }

  function advanceAfter(id: string, next: PlayerSetupDetail) {
    if (!(PLAYER_INTRO_STEPS as readonly string[]).includes(id)) {
      const ids = next.steps.map((step) => step.id)
      const following = ids[ids.indexOf(id) + 1]
      setStepId(following ?? "review")
      return
    }
    const index = PLAYER_INTRO_STEPS.indexOf(id as (typeof PLAYER_INTRO_STEPS)[number])
    const following = PLAYER_INTRO_STEPS[index + 1]
    if (following) {
      setStepId(following)
      return
    }
    setStepId(next.resumeStep || next.steps[0]?.id || "review")
  }

  async function saveBirth() {
    if (!birthIso || age == null || age < 16 || !terms) return
    const ok = await run(() => apiClient.playerSetup.saveBirthTerms(birthIso, true))
    if (ok) setStepId("player_agent_access")
  }

  async function saveAgents() {
    const grants = setup.agents.map((agent) => ({ userId: agent.id, allow: agentChoice[agent.id] === true }))
    const ok = await run(() => apiClient.playerSetup.saveAgents(grants))
    if (ok) setStepId("player_consents")
  }

  async function commitConsents() {
    setBusy(true)
    setError(null)
    try {
      const next = await apiClient.playerSetup.saveConsents({ ...consents, markStarted: true })
      apply(next)
      setConsentSheet(false)
      setStepId(next.resumeStep || "review")
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong")
    } finally {
      setBusy(false)
    }
  }

  async function saveFinancial() {
    const parsed = {
      netMonthlyIncome: parseMoney(income),
      monthlySavings: parseMoney(savings),
      liquidWealth: parseMoney(liquid),
    }
    if ([income, savings, liquid].some((value) => value.trim() && parseMoney(value) == null)) {
      setError("Enter amounts as numbers")
      return
    }
    setBusy(true)
    setError(null)
    try {
      const next = await apiClient.playerSetup.saveFinancial(parsed)
      apply(next)
      advanceAfter("player_financial", next)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong")
    } finally {
      setBusy(false)
    }
  }

  async function finishConnection(step: string, connected: boolean) {
    setBusy(true)
    setError(null)
    try {
      const next = await apiClient.playerSetup.finishStep(step, connected)
      apply(next)
      advanceAfter(step, next)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong")
    } finally {
      setBusy(false)
    }
  }

  const agentsReady = setup.agents.length > 0
    && setup.agents.every((agent) => agentChoice[agent.id] != null)
    && setup.agents.some((agent) => agentChoice[agent.id] === true)
  const birthReady = Boolean(birthIso && age != null && age >= 16 && terms)
  const nextDisabled = busy || (stepId === "player_birth_terms" && !birthReady) || (stepId === "player_agent_access" && !agentsReady)
  const nextLabel = stepId === "review"
    ? "Go to my profile"
    : lastTask
      ? "Finish"
      : (PLAYER_INTRO_STEPS as readonly string[]).includes(stepId)
        ? "Continue"
        : "Next ›"

  async function onNext() {
    if (stepId === "review") {
      onDismiss()
      return
    }
    if (stepId === "player_birth_terms") return saveBirth()
    if (stepId === "player_agent_access") return saveAgents()
    if (stepId === "player_consents") {
      if (skippedWhoop || skippedQuest) {
        setConsentSheet(true)
        return
      }
      return commitConsents()
    }
    if (stepId === "player_financial") return saveFinancial()
    if (stepId === "player_whoop") return finishConnection("player_whoop", whoopPhase === "connected")
    if (stepId === "player_instagram") return finishConnection("player_instagram", igPhase === "connected")
    if (stepId === "player_motivation") return finishConnection("player_motivation", motivationDone)
  }

  const sheetTitle = skippedWhoop && skippedQuest ? "You skipped both" : skippedWhoop ? "You skipped WHOOP data" : "You skipped the questionnaire"

  return (
    <OnboardingDialog
      open={open}
      onClose={() => { void leave() }}
      describedBy="player-setup-description"
      leaveTitle="Leave onboarding?"
      leaveBody={setup.started
        ? "Your progress so far is saved. You can continue later."
        : "You’ll be logged out. Your progress so far is saved, and you can continue from this step when you log in again."}
    >
      {(requestLeave) => (
        <div className="relative flex min-h-0 flex-1 flex-col gap-6 text-[#1F2328] dark:text-foreground">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <DialogTitle className="text-[22px] font-bold tracking-[-0.02em]">Player Onboarding</DialogTitle>
              <DialogDescription id="player-setup-description" className="text-[15px] text-[#6B7280] dark:text-muted-foreground">
                Complete the following steps to set up your player profile.
              </DialogDescription>
            </div>
            <button type="button" aria-label="Close onboarding" onClick={requestLeave} className="-mr-2.5 -mt-2.5 flex h-11 w-11 items-center justify-center text-[#6B7280]">
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {error ? <p className="mb-4 text-sm text-red-700">{error}</p> : null}
            {stepId === "player_birth_terms" ? (
              <section className="flex flex-col gap-4">
                <h2 className="text-[22px] font-bold tracking-[-0.02em]">Welcome to SigmAI</h2>
                <p className="text-sm leading-relaxed text-[#4B5563] dark:text-muted-foreground">Before you start, please confirm the following.</p>
                <label className="text-sm font-semibold" htmlFor="player-dob-day">Date of birth</label>
                <div className="flex gap-2">
                  <input id="player-dob-day" data-fixed-width inputMode="numeric" maxLength={2} placeholder="DD" value={day} onChange={(event) => setDay(event.target.value.replace(/\D/g, "").slice(0, 2))} className="h-11 w-16 min-w-0 rounded-[10px] border border-[#E5E7EB] px-3 text-center dark:border-border dark:bg-background" />
                  <input data-fixed-width aria-label="Month" inputMode="numeric" maxLength={2} placeholder="MM" value={month} onChange={(event) => setMonth(event.target.value.replace(/\D/g, "").slice(0, 2))} className="h-11 w-16 min-w-0 rounded-[10px] border border-[#E5E7EB] px-3 text-center dark:border-border dark:bg-background" />
                  <input data-fixed-width aria-label="Year" inputMode="numeric" maxLength={4} placeholder="YYYY" value={year} onChange={(event) => setYear(event.target.value.replace(/\D/g, "").slice(0, 4))} className="h-11 w-24 min-w-0 max-w-full rounded-[10px] border border-[#E5E7EB] px-3 text-center dark:border-border dark:bg-background" />
                </div>
                <p className="text-xs text-[#6B7280]">Filled in by your agent. Change it if it's wrong.</p>
                <label className="flex items-start gap-3 rounded-xl border border-[#E5E7EB] p-3.5 dark:border-border">
                  <input type="checkbox" checked={terms} onChange={(event) => setTerms(event.target.checked)} className="mt-1 h-5 w-5" />
                  <span className="text-[15px] leading-snug">
                    I accept the <LegalLink className="underline" href="/terms">Terms of Use</LegalLink> and have read the <LegalLink className="underline" href="/privacy">Privacy Statement</LegalLink>.
                  </span>
                </label>
                {tooYoung ? (
                  <div role="status" className="rounded-[10px] border border-[#E5E7EB] bg-[#F3F4F6] px-3.5 py-3 text-sm leading-relaxed dark:bg-muted">
                    SigmAI is only available to people aged 16 or older.
                  </div>
                ) : null}
              </section>
            ) : null}

            {stepId === "player_agent_access" ? (
              <section className="flex flex-col gap-4">
                <h2 className="text-[22px] font-bold tracking-[-0.02em]">Give your agents access to your account?</h2>
                {setup.agents.length === 0 ? (
                  <p className="text-sm text-[#6B7280]">No agent is connected to this account yet. Contact us and we'll sort it out.</p>
                ) : null}
                {setup.agents.length > 0 && setup.agents.every((agent) => agentChoice[agent.id] === false) ? (
                  <p className="text-sm text-[#6B7280]">Allow at least one agent to continue.</p>
                ) : null}
                <div className="flex flex-col gap-2.5 rounded-xl border border-[#E5E7EB] px-4 py-3.5 text-sm dark:border-border">
                  <div className="flex items-start gap-2.5 leading-normal">
                    <Check className="mt-px h-[18px] w-[18px] shrink-0 text-[#1B8A4B]" strokeWidth={2.5} aria-hidden />
                    <span>See and change all your data in SigmAI</span>
                  </div>
                  <div className="flex items-start gap-2.5 leading-normal">
                    <X className="mt-px h-[18px] w-[18px] shrink-0 text-[#9CA3AF]" strokeWidth={2.5} aria-hidden />
                    <span>Give or withdraw consent on your behalf</span>
                  </div>
                </div>
                {setup.agents.map((agent) => {
                  const name = agent.fullName || agent.email
                  const choice = agentChoice[agent.id]
                  return (
                    <div key={agent.id} className="flex flex-col gap-1.5">
                      <div className="flex flex-col gap-3.5 rounded-xl border border-[#E5E7EB] p-4 dark:border-border">
                        <div className="flex items-center gap-3">
                          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF2FC] text-[17px] font-bold text-[#3B5FC4]">{initials(name)}</span>
                          <span className="min-w-0">
                            <span className="block break-words text-base font-bold">{name}</span>
                            <span className="block break-all text-xs text-[#6B7280]">{agent.email}</span>
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                          <AgentChoiceButton pressed={choice === false} onClick={() => setAgentChoice((current) => ({ ...current, [agent.id]: false }))}>Don't allow</AgentChoiceButton>
                          <AgentChoiceButton pressed={choice === true} onClick={() => setAgentChoice((current) => ({ ...current, [agent.id]: true }))}>Allow access</AgentChoiceButton>
                        </div>
                      </div>
                      <a className="text-[13px] font-medium text-[#3B5FC4]" href="mailto:info@sigmasportsmanagement.nl">Not your agent? Contact us</a>
                    </div>
                  )
                })}
                <div className="rounded-[10px] border border-[#D6E1F8] bg-[#EEF3FD] px-3.5 py-3 text-sm text-[#2B4BB0]">
                  You can remove access at any time in Settings.
                </div>
              </section>
            ) : null}

            {stepId === "player_consents" ? (
              <ConsentStep
                adult={setup.adult}
                consents={consents}
                moreKey={consentMore}
                onToggle={(key) => setConsents((current) => ({ ...current, [key]: !current[key] }))}
                onMore={(key) => setConsentMore((current) => current === key ? null : key)}
                onAllowAll={() => setConsents((current) => ({
                  publicCareer: true,
                  whoop: true,
                  motivation: true,
                  aiAssistant: true,
                  improveModels: setup.adult ? true : current.improveModels,
                }))}
              />
            ) : null}

            {stepId === "player_financial" ? (
              <section className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-[22px] font-bold tracking-[-0.02em]">Financial Data</h2>
                  <button type="button" aria-expanded={help} onClick={() => setHelp((open) => !open)} className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-[#3B5FC4] text-sm font-bold text-[#3B5FC4]">?</button>
                </div>
                <p className="text-[15px] text-[#6B7280]">Update these once a month.</p>
                {help ? (
                  <div className="flex flex-col gap-3 rounded-xl bg-[#F7F8FA] p-4 text-sm dark:bg-muted">
                    <h3 className="font-semibold">Why we ask</h3>
                    <p>To help secure your financial freedom. We use these numbers to calculate until what age your money lasts.</p>
                    <p><strong>Not sure?</strong> A rough estimate is fine. Only you and your agent can see these numbers.</p>
                  </div>
                ) : null}
                <MoneyField label="Net monthly income (€)" value={income} onChange={setIncome} placeholder="e.g. 8,000" />
                <MoneyField label="Monthly savings (€)" value={savings} onChange={setSavings} placeholder="e.g. 2,000" />
                <MoneyField label="Available money / starting portfolio (€)" value={liquid} onChange={setLiquid} placeholder="e.g. 50,000" />
              </section>
            ) : null}

            {stepId === "player_whoop" ? (
              <section className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-[22px] font-bold tracking-[-0.02em]">WHOOP Integration</h2>
                  <button type="button" aria-expanded={help} onClick={() => setHelp((open) => !open)} className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-[#3B5FC4] text-sm font-bold text-[#3B5FC4]">?</button>
                </div>
                <p className="text-[15px] text-[#6B7280]">Connect your WHOOP account to sync your health and performance data.</p>
                {help ? (
                  <div className="rounded-xl bg-[#F7F8FA] p-4 text-sm dark:bg-muted">
                    <h3 className="mb-2 font-semibold">Before you connect</h3>
                    <ol className="list-decimal space-y-1 pl-5">
                      <li>You have a WHOOP account and wear the device.</li>
                      <li>Log in with your own WHOOP account.</li>
                      <li>On the WHOOP screen, tap Allow to give SigmAI access.</li>
                    </ol>
                  </div>
                ) : null}
                {whoopPhase === "error" ? (
                  <div className="rounded-xl border border-[#F6CDB3] bg-[#FEF3EC] p-4">
                    <p className="font-semibold text-[#8A3300]">WHOOP didn't give access</p>
                    <p className="mt-1 text-sm text-[#6E2A00]">The login was cancelled, or Allow wasn't tapped.</p>
                  </div>
                ) : null}
                {whoopPhase === "connected" ? (
                  <div className="rounded-xl border border-[#D6E1F8] bg-[#F5F8FE] p-5">
                    <p className="font-semibold">WHOOP is connected. Loading your data…</p>
                    <p className="mt-1 text-sm text-[#4B5563]">WHOOP is sending your recovery, sleep and strain. This can take a few minutes.</p>
                    <p className="mt-3 text-sm text-[#2B4BB0]"><strong>You don't have to wait.</strong> Continue when you want. We keep loading in the background.</p>
                  </div>
                ) : (
                  <WhoopConnectButton
                    playerId={setup.playerId}
                    backgroundSync
                    onSuccess={() => setWhoopPhase("connected")}
                    onError={() => setWhoopPhase("error")}
                    className="bg-[#3B5FC4]"
                  >
                    {whoopPhase === "error" ? "Try again" : "Connect WHOOP"}
                  </WhoopConnectButton>
                )}
              </section>
            ) : null}

            {stepId === "player_instagram" ? (
              <InstagramStep
                playerId={setup.playerId}
                phase={igPhase}
                handle={igHandle}
                onConnected={(handle) => {
                  setIgHandle(handle)
                  setIgPhase("connected")
                }}
                onError={() => setIgPhase("error")}
                onDisconnect={() => { setIgPhase("checks"); setIgHandle("") }}
              />
            ) : null}

            {stepId === "player_motivation" ? (
              <section className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-[22px] font-bold tracking-[-0.02em]">Motivation Questionnaire</h2>
                  <button type="button" aria-expanded={help} onClick={() => setHelp((open) => !open)} className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-[#3B5FC4] text-sm font-bold text-[#3B5FC4]">?</button>
                </div>
                <p className="text-[15px] text-[#6B7280]">Fill in your first one now. Takes about 2 minutes. After this, once a week.</p>
                {help ? (
                  <div className="rounded-xl bg-[#F7F8FA] p-4 text-sm dark:bg-muted">
                    <h3 className="mb-2 font-semibold">Why it matters</h3>
                    <p>It gives your agent insight into how you are doing, and lets us link it to your other data, such as fitness and performance.</p>
                    <p className="mt-2">There are no right or wrong answers. Go with your first feeling.</p>
                  </div>
                ) : null}
                {motivationDone ? (
                  <div className="rounded-xl border border-[#A8CDB8] bg-[#F2F8F4] p-6 text-center">
                    <p className="text-lg font-bold">First questionnaire done</p>
                    <p className="mt-2 text-sm text-[#4B5563]">Your agent can now see your first results. We'll remind you when the next one is due, in a week.</p>
                  </div>
                ) : (
                  <>
                    <MentalHealthQuestionnaire
                      playerId={setup.playerId}
                      onSave={() => {
                        setMotivationDone(true)
                        void apiClient.playerSetup.finishStep("player_motivation", true).then(apply)
                      }}
                    />
                    <button type="button" className="self-center text-[13px] text-[#6B7280] underline" onClick={() => { void finishConnection("player_motivation", false) }}>
                      Skip for now. You'll find it on the Motivation page.
                    </button>
                  </>
                )}
              </section>
            ) : null}

            {stepId === "review" ? <Review setup={setup} whoopLoading={whoopPhase === "connected"} igHandle={igHandle} motivationDone={motivationDone} /> : null}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-[#E5E7EB] pt-4 dark:border-border">
            {stepId === "review" ? <span /> : (
              <button type="button" className={quietClass} disabled={navIndex <= 0 || busy} onClick={() => navIndex > 0 && setStepId(nav[navIndex - 1])}>
                ‹ Previous
              </button>
            )}
            {phase.length > 0 ? (
              <div className="flex items-center gap-1.5" aria-label={`Step ${phaseIndex + 1} of ${phase.length}`}>
                {phase.map((id, index) => (
                  <span key={id} className={`h-2 rounded-full ${index === phaseIndex ? "w-8 bg-[#3B5FC4]" : "w-2 bg-[#E5E7EB] dark:bg-muted"}`} />
                ))}
              </div>
            ) : <span />}
            <button type="button" className={primaryClass} disabled={stepId !== "review" && nextDisabled} onClick={() => { void onNext() }}>
              {busy ? "Saving…" : nextLabel}
            </button>
          </div>
          <ConsentSkipSheet
            open={consentSheet}
            title={sheetTitle}
            body={`Without it, you and your agent can’t see ${skippedWhoop && skippedQuest ? "your fitness and recovery or your motivation" : skippedWhoop ? "your fitness and recovery" : "your motivation"}. You can turn it on later in Settings.`}
            onBack={() => setConsentSheet(false)}
            onContinue={() => { void commitConsents() }}
          />
        </div>
      )}
    </OnboardingDialog>
  )
}

function ConsentStep({
  adult,
  consents,
  moreKey,
  onToggle,
  onMore,
  onAllowAll,
}: {
  adult: boolean
  consents: PlayerSetupConsents
  moreKey: string | null
  onToggle: (key: keyof PlayerSetupConsents) => void
  onMore: (key: string) => void
  onAllowAll: () => void
}) {
  const rows = CONSENT_COPY.filter((item) => !item.adultOnly || adult)
  const allAllowed = rows.every((item) => consents[item.key])
  // Shown above and below the list, so players don't have to scroll to find it.
  const allowAllButton = (
    <button
      type="button"
      onClick={onAllowAll}
      className={
        "min-h-14 w-full rounded-xl text-[17px] font-bold " +
        (allAllowed
          ? "border-[1.5px] border-[#1B6B3A] bg-[#E7F5EC] text-[#1B6B3A]"
          : "border-0 bg-[#3B5FC4] text-white")
      }
    >
      {allAllowed ? "✓ All allowed" : "Allow all"}
    </button>
  )
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-[22px] font-bold tracking-[-0.02em]">Choose what you share</h2>
      <p className="text-sm leading-relaxed text-[#4B5563] dark:text-muted-foreground">These are optional, but some features need them. You can change this at any time in Settings.</p>
      {allowAllButton}
      <div className="text-xs font-bold uppercase tracking-[0.06em] text-[#6B7280]">Optional</div>
      {rows.map((item) => {
        const on = consents[item.key]
        return (
          <div key={item.key} className={`flex flex-col gap-1.5 rounded-xl p-4 ${on ? "border-[1.5px] border-[#3B5FC4] bg-[#F5F8FE]" : "border border-[#E5E7EB] bg-white dark:border-border dark:bg-card"}`}>
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-labelledby={`lbl-${item.key}`}
                onClick={() => onToggle(item.key)}
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 p-0 ${on ? "border-[#3B5FC4] bg-[#3B5FC4]" : "border-[#9CA3AF] bg-white dark:bg-card"}`}
              >
                {on ? <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.5} aria-hidden /> : null}
              </button>
              <span id={`lbl-${item.key}`} className="cursor-pointer text-[15px] font-bold" onClick={() => onToggle(item.key)}>{item.title}</span>
            </div>
            <p className="text-sm leading-normal">{item.short}</p>
            {moreKey === item.key ? <p className="text-[13px] leading-normal text-[#4B5563] dark:text-muted-foreground">{item.detail}</p> : null}
            <button type="button" aria-expanded={moreKey === item.key} className="self-start text-[13px] font-semibold text-[#3B5FC4]" onClick={() => onMore(item.key)}>
              {moreKey === item.key ? "Less" : "More"}
            </button>
            <p className="text-xs leading-normal text-[#6B7280]">{item.without}</p>
          </div>
        )
      })}
      {allowAllButton}
      <p className="text-center text-[13px] leading-normal text-[#6B7280]">Or tick only what you want and tap Continue.</p>
      <LegalLink className="text-sm font-medium text-[#3B5FC4]" href="/privacy">Read our Privacy Statement</LegalLink>
    </section>
  )
}

function ConsentSkipSheet({
  open,
  title,
  body,
  onBack,
  onContinue,
}: {
  open: boolean
  title: string
  body: string
  onBack: () => void
  onContinue: () => void
}) {
  useOnboardingEscape(open, onBack)
  if (!open) return null
  return (
    <div className="absolute inset-0 z-30 flex items-end bg-[rgba(17,24,39,0.45)]">
      <div role="dialog" aria-modal="true" aria-labelledby="skipped-consent-title" className="flex w-full flex-col gap-3.5 rounded-t-[20px] bg-white px-5 pb-6 pt-3 text-[#1F2328] dark:bg-card dark:text-foreground">
        <span aria-hidden className="mx-auto h-1 w-10 rounded-full bg-[#D1D5DB]" />
        <h3 id="skipped-consent-title" className="m-0 text-[19px] font-bold tracking-[-0.01em]">{title}</h3>
        <p className="m-0 text-sm leading-relaxed text-[#4B5563] dark:text-muted-foreground">{body}</p>
        <div className="grid grid-cols-2 gap-2.5 pt-1">
          <button type="button" onClick={onBack} className="min-h-12 rounded-[10px] border-[1.5px] border-[#3B5FC4] bg-[#3B5FC4] text-base font-semibold text-white">Go back</button>
          <button type="button" onClick={onContinue} className="min-h-12 rounded-[10px] border-[1.5px] border-[#1F2328] bg-white text-base font-semibold text-[#1F2328] dark:border-border dark:bg-card dark:text-foreground">Continue anyway</button>
        </div>
      </div>
    </div>
  )
}

function AgentChoiceButton({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={
        "flex min-h-12 items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-[#3B5FC4] px-2 text-[15px] font-semibold " +
        (pressed ? "bg-[#3B5FC4] text-white" : "bg-white text-[#3B5FC4] dark:bg-card")
      }
    >
      {pressed ? <Check className="h-4 w-4" strokeWidth={3} aria-hidden /> : null}
      {children}
    </button>
  )
}

function MoneyField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm font-medium">
      {label}
      <input value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} className="box-border h-11 w-full min-w-0 max-w-full rounded-[10px] border border-[#E5E7EB] px-3.5 font-normal dark:border-border dark:bg-background" />
    </label>
  )
}

function InstagramMark({ className = "h-9 w-9" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  )
}

function InstagramStep({
  playerId,
  phase,
  handle,
  onConnected,
  onError,
  onDisconnect,
}: {
  playerId: string
  phase: "checks" | "error" | "connected"
  handle: string
  onConnected: (handle: string) => void
  onError: () => void
  onDisconnect: () => void
}) {
  const [helpOpen, setHelpOpen] = useState(phase === "checks")
  const [step, setStep] = useState(0)
  const [how, setHow] = useState(false)
  const [copied, setCopied] = useState(false)
  const checksDone = step >= IG_CHECKS.length
  const check = IG_CHECKS[Math.min(step, IG_CHECKS.length - 1)]
  const last = step === IG_CHECKS.length - 1
  const connectedAs = handle ? `@${handle.replace(/^@/, "")}` : ""

  function connectSucceeded() {
    void apiClient.instagram.getByPlayer(playerId).then((row) => {
      onConnected(row?.igUsername || handle)
    }).catch(() => onConnected(handle))
  }

  return (
    <section className="flex flex-col gap-5 rounded-[14px] border border-[#E5E7EB] p-6 dark:border-border">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2.5">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <circle cx="12" cy="10" r="3" />
            <path d="M7 21v-1a5 5 0 0 1 10 0v1" />
          </svg>
          <h2 className="text-[22px] font-bold tracking-[-0.02em]">Instagram Integration</h2>
          <button
            type="button"
            aria-label="How does connecting Instagram work?"
            aria-expanded={phase === "checks" && helpOpen}
            onClick={() => { if (phase === "checks") setHelpOpen((open) => !open) }}
            className={
              "ml-0.5 flex h-[30px] w-[30px] items-center justify-center rounded-full text-sm font-bold " +
              (phase === "checks" && helpOpen ? "border border-[#3B5FC4] bg-[#3B5FC4] text-white" : "border border-[#D1D5DB] bg-white text-[#6B7280] dark:bg-card")
            }
          >
            ?
          </button>
        </div>
        {phase === "checks" ? (
          <>
            <p className="text-base font-semibold leading-snug">Show clubs and sponsors what you're worth off the pitch.</p>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {[["Reach", "Who sees you"], ["Engagement", "How fans react"], ["Value", "Backed by numbers"]].map(([title, caption]) => (
                <div key={title} className="flex flex-col gap-0.5 rounded-[10px] bg-[#F7F8FA] p-3 dark:bg-muted">
                  <div className="text-sm font-bold">{title}</div>
                  <div className="text-xs leading-snug text-[#6B7280]">{caption}</div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-[15px] leading-normal text-[#6B7280]">Connect your Instagram account to sync your social media data.</p>
        )}
      </div>

      {phase === "error" ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5 rounded-xl border border-[#F6CDB3] bg-[#FEF3EC] p-4">
            <p className="font-semibold text-[#8A3300]">The connection didn't go through</p>
            <p className="text-sm leading-normal text-[#6E2A00]">We couldn't find a Facebook Page linked to your account{connectedAs ? ` ${connectedAs}` : ""}.</p>
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-[15px] font-semibold">How to fix it</h3>
            <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-normal">
              <li>In Instagram, tap Edit profile.</li>
              <li>Under business information, tap Page.</li>
              <li>Connect an existing Facebook Page, or create a new one.</li>
              <li>Come back here and tap Try again.</li>
            </ol>
          </div>
          <p className="text-sm leading-normal text-[#6B7280]">Someone else manages this Instagram? Send them these steps.</p>
          <InstagramConnectButton playerId={playerId} showIcon={false} onSuccess={connectSucceeded} onError={onError} className="h-11 border-0 bg-gradient-to-r from-[#9D4EDD] via-[#D9437F] to-[#E07B2E] text-base font-semibold text-white hover:opacity-95">
            Try again
          </InstagramConnectButton>
          <button
            type="button"
            className="flex min-h-11 items-center justify-center gap-2 rounded-[10px] border border-[#D1D5DB] bg-white text-[15px] font-semibold text-[#1F2328] dark:bg-card dark:text-foreground"
            onClick={() => {
              void navigator.clipboard?.writeText("1. In Instagram, tap Edit profile.\n2. Under business information, tap Page.\n3. Connect an existing Facebook Page, or create a new one.\n4. Come back and try again.")
              setCopied(true)
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
            {copied ? "Copied" : "Copy steps"}
          </button>
          <p className="text-center text-sm leading-normal text-[#4B5563]">
            Still stuck? Email us at <a className="font-semibold" href="mailto:info@sigmasportsmanagement.nl?subject=Help%20connecting%20Instagram&body=My%20Instagram%20account%3A%20%40%0AMy%20phone%20number%3A%20">info@sigmasportsmanagement.nl</a> with your phone number, and we'll call you back to help you connect.
          </p>
        </div>
      ) : null}

      {phase === "connected" ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#A855F7] via-[#E1497F] to-[#F0954A] text-white">
            <InstagramMark />
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E7F5EC] px-3 py-1 text-sm font-semibold text-[#1B6B3A]">
            <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
            Connected
          </span>
          <p className="text-xl font-bold tracking-[-0.02em]">{connectedAs || "Instagram"}</p>
          <p className="text-[15px] leading-normal text-[#6B7280]">Tracking posts and stories from today. Earlier stories can't be added.</p>
          <button type="button" className={quietClass} onClick={() => { void apiClient.instagram.disconnect(playerId).then(onDisconnect) }}>
            Disconnect
          </button>
        </div>
      ) : null}

      {phase === "checks" ? (
        <>
          {helpOpen ? (
            <div className="flex flex-col gap-4 rounded-xl bg-[#F7F8FA] p-[18px] dark:bg-muted">
              <div className="flex flex-col gap-2.5">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-[15px] font-semibold">Before you connect</h3>
                  <span className="text-[13px] text-[#6B7280]">{checksDone ? "Done" : `Step ${step + 1} of 3`}</span>
                </div>
                <div className="grid grid-cols-3 gap-1.5" aria-hidden>
                  {[0, 1, 2].map((index) => (
                    <span
                      key={index}
                      className="h-1.5 rounded-full"
                      style={{ background: index < step || checksDone ? "#3B5FC4" : index === step ? "#9DB2E8" : "#E5E7EB" }}
                    />
                  ))}
                </div>
              </div>
              {!checksDone ? (
                <div className="flex flex-col gap-3.5 rounded-[10px] border border-[#E5E7EB] bg-white p-[18px] dark:border-border dark:bg-card">
                  <div className="flex items-start gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EEF2FC] text-[13px] font-bold text-[#3B5FC4]">{step + 1}</span>
                    <span>
                      <span className="block text-base font-semibold">{check.title}</span>
                      <span className="mt-1 block text-sm leading-normal text-[#6B7280]">{check.sub}</span>
                    </span>
                  </div>
                  {how ? (
                    <div className="ml-10 flex flex-col gap-1.5 rounded-lg bg-[#F7F8FA] px-3.5 py-3 dark:bg-muted">
                      <div className="text-[13px] font-semibold">How to check</div>
                      <ol className="list-decimal space-y-1.5 pl-4 text-sm leading-snug">
                        {check.steps.map((item) => <li key={item}>{item}</li>)}
                      </ol>
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-2 pl-10">
                    {how ? (
                      <button type="button" className="min-h-10 rounded-lg bg-[#3B5FC4] px-4 text-sm font-semibold text-white" onClick={() => { setHow(false); setStep(step + 1) }}>
                        {last ? "Done, finish" : "Done, next step"}
                      </button>
                    ) : (
                      <>
                        <button type="button" className="min-h-10 rounded-lg bg-[#3B5FC4] px-4 text-sm font-semibold text-white" onClick={() => { setHow(false); setStep(step + 1) }}>
                          {last ? "Yes, finish" : "Yes, next step"}
                        </button>
                        <button type="button" className="min-h-10 rounded-lg border border-[#D1D5DB] bg-white px-4 text-sm font-semibold text-[#1F2328] dark:bg-card dark:text-foreground" onClick={() => setHow(true)}>
                          Not sure
                        </button>
                      </>
                    )}
                    {step > 0 || how ? (
                      <button
                        type="button"
                        className="min-h-10 px-3 text-sm font-medium text-[#3B5FC4]"
                        onClick={() => { if (how) setHow(false); else { setStep(Math.max(0, step - 1)); setHow(false) } }}
                      >
                        Back
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3.5 rounded-[10px] border border-[#E5E7EB] bg-white p-[18px] dark:border-border dark:bg-card">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E7F5EC] text-[#1B6B3A]">
                      <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
                    </span>
                    <span className="text-base font-semibold">All set. Connect below.</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-[13px] leading-relaxed">
                    <div>
                      <h4 className="mb-1 font-semibold">SigmAI can see</h4>
                      <ul className="list-disc pl-4">
                        <li>Posts and stories</li>
                        <li>Reach, views and interactions</li>
                        <li>Follower count</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="mb-1 font-semibold">SigmAI can't</h4>
                      <ul className="list-disc pl-4">
                        <li>Post anything</li>
                        <li>Read or send messages</li>
                        <li>Change your account</li>
                      </ul>
                    </div>
                  </div>
                  <div className="rounded-lg border border-[#D6E1F8] bg-[#EEF3FD] px-3.5 py-3 text-[13px] leading-normal text-[#2B4BB0]">
                    <strong>Good to know:</strong> stories are tracked from the moment you connect. Instagram only keeps story stats for 24 hours, so older stories can't be added. Stories with fewer than 5 viewers show no stats.
                  </div>
                  <button type="button" className="self-start text-sm font-medium text-[#3B5FC4]" onClick={() => { setStep(0); setHow(false) }}>
                    Start the checks again
                  </button>
                </div>
              )}
            </div>
          ) : null}
          <div className="flex flex-col items-center gap-3.5 px-0 py-2 text-center">
            <span className="flex h-[72px] w-[72px] items-center justify-center rounded-[18px] bg-gradient-to-br from-[#A855F7] via-[#E1497F] to-[#F0954A] text-white">
              <InstagramMark />
            </span>
            <div className="text-xl font-bold tracking-[-0.02em]">No Instagram connection</div>
            <p className="max-w-[380px] text-[15px] leading-normal text-[#4B5563]">You log in with Facebook. The Instagram linked to it comes along automatically.</p>
            <InstagramConnectButton playerId={playerId} showIcon={false} onSuccess={connectSucceeded} onError={onError} className="h-12 border-0 bg-[#1877F2] px-6 text-base font-semibold text-white hover:bg-[#1877F2]/90">
              Continue with Facebook
            </InstagramConnectButton>
            <p className="text-[13px] text-[#6B7280]">About 5 minutes · We can't post or read your DMs</p>
          </div>
        </>
      ) : null}
    </section>
  )
}

function Review({
  setup,
  whoopLoading,
  igHandle,
  motivationDone,
}: {
  setup: PlayerSetupDetail
  whoopLoading: boolean
  igHandle: string
  motivationDone: boolean
}) {
  const rows = setup.steps.map((step) => {
    const skipped = step.status === "skipped"
    const whoop = step.id === "player_whoop"
    return {
      title: step.title,
      detail: whoop && whoopLoading && !skipped
        ? "Still loading your data. No need to wait."
        : step.id === "player_instagram" && !skipped
          ? `Connected as @${(igHandle || setup.instagramUsername || "instagram").replace(/^@/, "")}`
          : step.id === "player_motivation" && motivationDone
            ? "Next one in a week"
            : skipped
              ? "Skipped"
              : step.id === "player_financial"
                ? "Update once a month"
                : "Done",
      loading: whoop && whoopLoading && !skipped,
      skipped,
    }
  })
  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-col items-center gap-2 py-2 text-center">
        <h2 className="text-[22px] font-bold">You're all set</h2>
        <p className="text-[15px] text-[#4B5563] dark:text-muted-foreground">Your agent sees your data as soon as it comes in.</p>
      </div>
      <ol className="flex list-none flex-col gap-2.5 p-0">
        {rows.map((row) => (
          <li key={row.title} className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 ${row.loading ? "border-[#D6E1F8] bg-[#F5F8FE]" : "border-[#E5E7EB] dark:border-border"}`}>
            <span className="flex-1">
              <span className="block text-[15px] font-semibold">{row.title}</span>
              <span className="block text-[13px] text-[#6B7280]">{row.detail}</span>
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${row.loading ? "bg-[#EEF2FC] text-[#2B4BB0]" : row.skipped ? "bg-[#F3F4F6] text-[#4B5563]" : "bg-[#E7F5EC] text-[#1B6B3A]"}`}>
              {row.loading ? "Loading" : row.skipped ? "Skipped" : "Done"}
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}
